import { matchPath, useLocation, useParams } from 'react-router-dom';
import { organizationPath, organizationsPath, organizationsNewPath } from '@app/constants/url';

/** Matches any route under `/app/organizations/:organizationId/…` */
const orgScopedPattern = `${organizationPath(':organizationId')}/*`;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`);
}

const ORG_IN_PATH = new RegExp(`^${escapeRegex(organizationsPath)}/([^/]+)(/|$)`);

const RESERVED_ORG_ID_SEGMENTS: ReadonlySet<string> = new Set([organizationsNewPath.split('/').pop()!]);

function sanitizeOrgIdSegment(id: string | undefined): string | undefined {
  if (id == null) return undefined;
  if (RESERVED_ORG_ID_SEGMENTS.has(id)) return undefined;
  return id;
}

/** First org-scoped path segment, or undefined (excludes e.g. `new` from /organizations/new). */
export function getOrganizationIdFromPathname(pathname: string): string | undefined {
  return sanitizeOrgIdSegment(pathname.match(ORG_IN_PATH)?.[1]);
}

/**
 * With nested `<Routes>` using absolute paths, `useParams()` sometimes omits parent
 * params. Prefer pathname matching so `organizationId` always reflects the URL.
 */
export function useOrganizationId(): string | undefined {
  const { pathname } = useLocation();
  const { organizationId: fromParams } = useParams<{ organizationId?: string }>();
  if (fromParams) return sanitizeOrgIdSegment(fromParams);
  const matched = matchPath({ path: orgScopedPattern, end: false }, pathname);
  const id = matched?.params?.organizationId;
  if (typeof id === 'string') return sanitizeOrgIdSegment(id);
  const fromPath = pathname.match(ORG_IN_PATH);
  return sanitizeOrgIdSegment(fromPath?.[1]);
}
